import { apiClient } from "@/services/api"
import { defineStore } from "pinia"

/** Keep in sync with the backend `com.lewara.backend.domain.worktimemodel.WorkTimeModelType`. */
export const WORK_TIME_MODEL_TYPES = ["FULL_TIME", "PART_TIME", "SHIFT"] as const
export type WorkTimeModelType = (typeof WORK_TIME_MODEL_TYPES)[number]

/** Keep in sync with the backend `com.lewara.backend.domain.util.WEEKDAYS`. */
export const WEEKDAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"] as const
export type Weekday = (typeof WEEKDAYS)[number]

export interface WorkDayPattern {
  targetHours: string | number
  plannedFrom: string | null
  plannedTo: string | null
  note: string | null
}

/**
 * "Once a day is longer than `afterHours`, at least `minMinutes` of it are break."
 * German § 4 ArbZG states this twice: 30 minutes past six hours, 45 past nine.
 */
export interface BreakRule {
  afterHours: string | number
  minMinutes: number
}

export interface ShiftTemplate {
  /** Null while the row is new; the server mints the id and it must be sent back unchanged after. */
  id: string | null
  name: string
  fromTime: string
  toTime: string
}

export interface WorkTimeModel {
  id: string
  name: string
  type: WorkTimeModelType
  color: string
  weeklyTargetHours: string | number
  days: Partial<Record<Weekday, WorkDayPattern>>
  breakRules: BreakRule[]
  maxDailyHours: string | number
  roundingMinutes: number | null
  shiftTemplates: ShiftTemplate[]
  /**
   * The Standardmodell: what the employee form prefills for a new employee. At most one per workspace
   * (the backend unsets the previous one), possibly none. A prefill only — nothing resolves a model
   * from it, and an employee without an assignment still has none.
   */
  isDefault?: boolean
  createdAt?: string
  lastModifiedAt?: string | null
  lastModifiedBy?: string | null
  /** Set only while archived (docs/archiving.md). */
  archivedAt?: string | null
  archivedBy?: string | null
}

export interface WorkTimeModelAssignment {
  modelId: string
  validFrom: string
  /** Null for the current, open-ended arrangement. */
  validTo: string | null
}

/** Somebody with no model from today on, from `GET /work-time-model/unassigned`. */
export interface UnassignedEmployee {
  id: string
  firstName: string
  lastName: string
}

/** § 4 ArbZG — what a new model starts with, matching what the backend seeds. */
export const STATUTORY_BREAK_RULES: BreakRule[] = [
  { afterHours: 6, minMinutes: 30 },
  { afterHours: 9, minMinutes: 45 },
]

export const blankWorkTimeModel = (): WorkTimeModel => ({
  id: "",
  name: "",
  type: "FULL_TIME",
  color: "#1976d2",
  weeklyTargetHours: 40,
  days: {
    MONDAY: { targetHours: 8, plannedFrom: null, plannedTo: null, note: null },
    TUESDAY: { targetHours: 8, plannedFrom: null, plannedTo: null, note: null },
    WEDNESDAY: { targetHours: 8, plannedFrom: null, plannedTo: null, note: null },
    THURSDAY: { targetHours: 8, plannedFrom: null, plannedTo: null, note: null },
    FRIDAY: { targetHours: 8, plannedFrom: null, plannedTo: null, note: null },
  },
  breakRules: [...STATUTORY_BREAK_RULES],
  // § 3 ArbZG.
  maxDailyHours: 10,
  roundingMinutes: null,
  shiftTemplates: [],
  isDefault: false,
})

export const useWorkTimeModelStore = defineStore("workTimeModel", {
  state: () => ({
    models: [] as WorkTimeModel[],
    /**
     * The caller's *own* models, from `/work-time-model/me`.
     *
     * Kept apart from [models] rather than filling it, because the two lists answer different
     * questions and only one of them needs a permission: [models] is the workspace's master data
     * (VIEW_WORK_TIME_MODEL — the settings tab manages it, the assignment dropdown picks from it),
     * while this is the handful of models the caller has worked to, which "Meine Zeiten" needs and
     * every employee may read. Loading the narrow list into the wide one would mean a visit to
     * "Meine Zeiten" leaving an admin's settings tab showing two models out of nine.
     */
    ownModels: [] as WorkTimeModel[],
    /**
     * The archived models (docs/archiving.md), loaded with [models]: no assignment is made from them any
     * more, but every day recorded under one and every past assignment still names it, so [byId] finds them.
     */
    archivedModels: [] as WorkTimeModel[],
    model: blankWorkTimeModel(),
  }),
  getters: {
    getModels: state => state.models,
    getModel: state => state.model,
    /**
     * Lookup for the recording and review screens, which render a model's name against an entry.
     *
     * Searches the master list first and the caller's own second, so a screen resolves an id from
     * whichever list it was allowed to load — and a reviewer, who has both, is unaffected.
     */
    byId: state => (id: string | null) =>
      state.models.find(it => it.id === id) ??
      state.archivedModels.find(it => it.id === id) ??
      state.ownModels.find(it => it.id === id) ??
      null,
    /** The Standardmodell of the master list, or null when the workspace has none. */
    defaultModel: state => state.models.find(it => it.isDefault) ?? null,
  },
  actions: {
    clearModel() {
      this.model = blankWorkTimeModel()
    },
    async loadModels() {
      // One request for both: the archived ones only ever serve name lookups.
      const res = await apiClient.get("/work-time-model", {
        params: { archive: "ALL" },
        headers: { tenant: true, authorization: true },
      })
      const all: WorkTimeModel[] = res.data ?? []
      this.models = all.filter(it => !it.archivedAt)
      this.archivedModels = all.filter(it => it.archivedAt)
      return res
    },
    /**
     * Loads the list once per page, the way every other lookup list in this app does — the recording
     * screen needs the names but has no reason to refetch them on every keystroke.
     */
    async ensureLoaded() {
      if (this.models.length === 0) await this.loadModels()
    },
    /**
     * The caller's own models — what a self-service screen must use.
     *
     * `/work-time-model` requires VIEW_WORK_TIME_MODEL, which most staff do not hold: the add-on's
     * migration granted it alongside VIEW_USER_POSITION_GENERAL, so an ordinary employee's request
     * 403'd, `byId` returned null, and the entry dialog lost both its shift list and its target
     * figures. `/work-time-model/me` asks only for RECORD_OWN_TIME, the right "Meine Zeiten" itself
     * carries, and answers with the models that employee has actually been assigned.
     */
    async loadOwnModels() {
      const res = await apiClient.get("/work-time-model/me", { headers: { tenant: true, authorization: true } })
      this.ownModels = res.data
      return res
    },
    async ensureOwnLoaded() {
      if (this.ownModels.length === 0) await this.loadOwnModels()
    },
    async loadModel(id: string) {
      const res = await apiClient.get(`/work-time-model/${id}`, { headers: { tenant: true, authorization: true } })
      this.model = res.data
      return res
    },
    async createModel(model: WorkTimeModel) {
      const res = await apiClient.post("/work-time-model", model, {
        headers: { tenant: true, authorization: true },
      })
      if (res.status === 201) {
        this.unmarkOthers(res.data)
        this.models.push(res.data)
      }
      return res
    },
    async updateModel(model: WorkTimeModel) {
      const res = await apiClient.put(`/work-time-model/${model.id}`, model, {
        headers: { tenant: true, authorization: true },
      })
      this.unmarkOthers(res.data)
      const index = this.models.findIndex(it => it.id === model.id)
      if (index !== -1) this.models.splice(index, 1, res.data)
      return res
    },
    /**
     * The backend takes the Standardmodell mark off every other model when one is saved marked; the
     * list follows without a reload, so the old one's chip goes the moment the new one's appears.
     */
    unmarkOthers(saved: WorkTimeModel) {
      if (!saved?.isDefault) return
      this.models.forEach(it => {
        if (it.id !== saved.id) it.isDefault = false
      })
    },

    // --- assigning a model to an employee ------------------------------------------------------

    /**
     * Who has no model from today on: nothing in force today and nothing starting later, leavers left
     * out. Needs VIEW_USER_GENERAL besides VIEW_WORK_TIME_MODEL, since the answer is a list of names.
     */
    async loadUnassigned(): Promise<UnassignedEmployee[]> {
      const res = await apiClient.get("/work-time-model/unassigned", {
        headers: { tenant: true, authorization: true },
      })
      return res.data
    },

    async loadAssignments(userId: string) {
      const res = await apiClient.get(`/work-time-model/assignment/${userId}`, {
        headers: { tenant: true, authorization: true },
      })
      return res.data
    },
    /**
     * Replaces an employee's whole assignment history. The backend refuses overlapping ranges, so the
     * caller edits the list as a whole rather than appending — which is also how the UI presents it.
     *
     * @param timeAccount the Arbeitszeitkonto's seed — `{ balance, date }`, or nulls for none.
     *
     *   It rides with the assignments because it belongs to the same question and is known at the
     *   same moment: whoever types "worked to Vollzeit from 01.01." is the person who knows the
     *   employee arrived carrying 37 hours of overtime. Two tabs away it would never be filled in,
     *   and an account with no seed quietly reports everybody's Gleitzeit as starting at zero on the
     *   day the workspace switched over.
     */
    async saveAssignments(
      userId: string,
      assignments: WorkTimeModelAssignment[],
      timeAccount: { balance: string | number | null; date: string | null } = { balance: null, date: null }
    ) {
      // A seed is a *pair*, and both halves have to travel together. A balance with no date is
      // refused outright by the backend; a date with no balance is worse, because it is accepted —
      // and `FrontendTimeAccountService` then drops every day recorded before it from the account,
      // leaving the employee's whole history behind a carry-over of zero. So an incomplete pair is
      // sent as no seed at all rather than as half of one.
      const balance = Number(timeAccount.balance)
      const seeded =
        timeAccount.balance !== null &&
        timeAccount.balance !== undefined &&
        timeAccount.balance !== "" &&
        !Number.isNaN(balance) &&
        !!timeAccount.date
      const res = await apiClient.put(
        `/work-time-model/assignment/${userId}`,
        {
          assignments,
          timeAccountOpeningBalance: seeded ? balance : null,
          timeAccountOpeningDate: seeded ? timeAccount.date : null,
        },
        { headers: { tenant: true, authorization: true } }
      )
      return res.data
    },
  },
})
